import type { Ref } from 'react'
import type { NavigateFn } from '../types'

type StepAccessLoadingStateProps = {
  containerRef: Ref<HTMLDivElement>
}

export function StepAccessLoadingState({ containerRef }: StepAccessLoadingStateProps) {
  return (
    <div className="bg-white px-4 py-10">
      <div className="mx-auto max-w-4xl px-6 pb-20 md:px-12">
        <div
          ref={containerRef}
          tabIndex={-1}
          className="mx-auto max-w-[42rem] overflow-hidden rounded-[30px] border border-[#FFCAAA] bg-[linear-gradient(135deg,#FFF0E8_0%,#FFFFFF_55%,#F8F8F8_100%)] shadow-[0_18px_45px_rgba(230, 83, 0,0.12)] focus:outline-none"
        >
          <div className="relative px-6 py-10 text-center md:px-10 md:py-12">
            <div className="absolute inset-x-0 top-0 h-1.5 bg-[linear-gradient(90deg,#E65300_0%,#FF8855_50%,#E65300_100%)]" />

            <div className="mx-auto mb-4 inline-flex items-center gap-2 rounded-full border border-[#FFB080] bg-white/80 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.28em] text-[#B84500] shadow-sm">
              <span className="h-2 w-2 rounded-full bg-[#E65300] animate-pulse" />
              Secure progress check
            </div>

            <h2 className="text-[28px] font-semibold leading-tight text-[#2F241C]">
              Preparing your next enrollment step
            </h2>

            <p className="mx-auto mt-3 max-w-[30rem] text-sm leading-6 text-[#6A625B] md:text-[15px]">
              We&apos;re verifying your saved progress and making sure this step is available
              before we move you forward.
            </p>

            <div className="mx-auto mt-7 flex max-w-[18rem] items-center justify-center gap-2">
              <div className="h-2 flex-1 rounded-full bg-[#FFD9C0]">
                <div className="h-full w-2/3 rounded-full bg-[#E65300] animate-pulse" />
              </div>
              <div
                className="h-2.5 w-2.5 rounded-full bg-[#E65300] animate-pulse"
                style={{ animationDelay: '120ms' }}
              />
              <div
                className="h-2.5 w-2.5 rounded-full bg-[#F07843] animate-pulse"
                style={{ animationDelay: '240ms' }}
              />
              <div
                className="h-2.5 w-2.5 rounded-full bg-[#F0A080] animate-pulse"
                style={{ animationDelay: '360ms' }}
              />
            </div>

            <p className="mt-4 text-xs font-medium uppercase tracking-[0.22em] text-[#8C4000]">
              This should only take a moment
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

type StepValidationErrorStateProps = {
  containerRef: Ref<HTMLDivElement>
  message: string
  navigate: NavigateFn
}

export function StepValidationErrorState({
  containerRef,
  message,
  navigate
}: StepValidationErrorStateProps) {
  return (
    <div className="bg-white px-4 py-10">
      <div className="mx-auto max-w-4xl px-6 pb-20 md:px-12">
        <div
          ref={containerRef}
          tabIndex={-1}
          className="mx-auto max-w-[42rem] rounded-[26px] border border-[#D2D2D2] bg-[#F8F8F8] px-6 py-10 text-center shadow-sm focus:outline-none"
        >
          <p className="text-sm text-red-600">{message}</p>
          <button
            type="button"
            onClick={() => navigate('/application')}
            className="mt-4 text-sm font-medium text-primary hover:underline"
          >
            Back to application
          </button>
        </div>
      </div>
    </div>
  )
}
